import { FeatureCollection } from 'geojson'
import jmespath from 'jmespath'
import { get, map, sumBy } from 'lodash'
import { MapProperty } from '~map-preview/types/__generated/gql/graphql'

const getGeojsonSearch = (geojson: FeatureCollection, args: string) => {
  return jmespath.search(geojson.features, args)
}

const getSelectedPropertiesArgs = (aggregateKeys: string[]) => {
  if (aggregateKeys?.length < 1) return 'properties: properties'

  // INFO: map select properties from geojson features' properties
  const mapSelectPropertyKeys = aggregateKeys
    .map((key) => `"${key}": properties."${key}"`)
    .join(', ')

  return `properties: { ${mapSelectPropertyKeys} }`
}

const getSearchArgs = (
  mscode: string,
  aggregate: string,
  aggregateKeys: string[]
) => {
  const key = `MS_${aggregate.replace(':', '-')}`

  const selectedPropertiesArgs = getSelectedPropertiesArgs(aggregateKeys)

  const args = `[?(properties."${key}"=='${mscode}')].{ ${selectedPropertiesArgs} }`

  return args
}

type Feature = {
  properties: { mscode: string }
  sourceLayer: string
}

export const getBoundaryFeature = (
  geojson: FeatureCollection,
  feature: Feature,
  numericProperties: MapProperty[],
  aggregateLabel
) => {
  // INFO: get mscode, msname from base-map layer feature
  const mscode = get(feature, 'properties.mscode')
  const msname = get(feature, 'properties.msname')

  if (!mscode) return

  const aggregateKeys = map(numericProperties, 'name')

  // INFO: sourceLayer from base-map layer
  const { sourceLayer } = feature
  const args = getSearchArgs(mscode, sourceLayer, aggregateKeys)

  const properties = getGeojsonSearch(geojson, args)

  // INFO: For click outside of boundary (click on base-map layer)
  if (properties.length < 1) return

  // INFO: Add Location count, Name, Total properties to feature properties
  const locationCount = properties.length
  const totalAggregateProperties = {
    'Location count': locationCount,
    [aggregateLabel]: msname
  }

  numericProperties.forEach((property) => {
    const { name, options } = property
    const aggregate = get(options, 'aggregate', 'sum')

    // INFO: Calculate total sum of boundary properties
    const totalSum = sumBy(properties, `properties.${name}`)

    // INFO: map sum or average of boundary properties
    const key = aggregate === 'sum' ? `Total ${name}` : `Average ${name}`
    const value = aggregate === 'sum' ? totalSum : totalSum / locationCount

    totalAggregateProperties[key] = value
  })

  return {
    ...feature,
    properties: totalAggregateProperties
  }
}
